import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { analytesApi, studiesApi, studyAnalytesApi } from "@/lib/api";
import { toast } from "sonner";
import { Microscope, Plus, Pencil, Trash2, Search, X, FlaskConical, Link2, Save, FileText, Type, Calculator, File, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";

// Result type labels
const RESULT_TYPE_LABELS = {
  subtitle: "Subtítulo",
  text: "Texto",
  referenced_value: "Valor Referenciado",
  document: "Documento",
};

// Age unit options
const AGE_UNITS = [
  { value: "days", label: "Días" },
  { value: "months", label: "Meses" },
  { value: "years", label: "Años" },
];

export default function Analytes() {
  // All analytes (master list - independent)
  const [allAnalytes, setAllAnalytes] = useState([]);
  const [analyteSearchTerm, setAnalyteSearchTerm] = useState("");
  
  // Studies
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState("");
  const [studySearchTerm, setStudySearchTerm] = useState("");
  const [showStudyDropdown, setShowStudyDropdown] = useState(false);
  const [highlightedStudyIndex, setHighlightedStudyIndex] = useState(0);
  
  // Temporary list of analytes for the selected study (not saved yet)
  const [pendingAnalytes, setPendingAnalytes] = useState([]);
  const [originalAnalytes, setOriginalAnalytes] = useState([]); // To track changes
  const [hasChanges, setHasChanges] = useState(false);
  
  // Analyte search for adding to study
  const [addAnalyteSearchTerm, setAddAnalyteSearchTerm] = useState("");
  const [showAddAnalyteDropdown, setShowAddAnalyteDropdown] = useState(false);
  const [highlightedAnalyteIndex, setHighlightedAnalyteIndex] = useState(0);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnalyte, setEditingAnalyte] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    result_type: "referenced_value",
    unit: "",
    reference_min: "",
    reference_max: "",
    reference_text: "",
    text_options: "",  // Options for text type separated by comma
    age_sex_references: [],
    document_template: "",
    active: true,
  });

  // Refs
  const studySearchRef = useRef(null);
  const addAnalyteSearchRef = useRef(null);

  useEffect(() => {
    fetchStudies();
    fetchAllAnalytes();
  }, []);

  useEffect(() => {
    if (selectedStudy) {
      loadStudyAnalytes(selectedStudy);
    } else {
      setPendingAnalytes([]);
      setOriginalAnalytes([]);
      setHasChanges(false);
    }
  }, [selectedStudy]);

  // Check for changes (including order changes)
  useEffect(() => {
    // Compare IDs in order (not sorted) to detect position changes
    const pendingIds = pendingAnalytes.map(a => a.id).join(',');
    const originalIds = originalAnalytes.map(a => a.id).join(',');
    setHasChanges(pendingIds !== originalIds);
  }, [pendingAnalytes, originalAnalytes]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (studySearchRef.current && !studySearchRef.current.contains(event.target)) {
        setShowStudyDropdown(false);
      }
      if (addAnalyteSearchRef.current && !addAnalyteSearchRef.current.contains(event.target)) {
        setShowAddAnalyteDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchStudies = async () => {
    try {
      const response = await studiesApi.getAll();
      setStudies(response.data);
    } catch (error) {
      toast.error("Error al cargar estudios");
    }
  };

  const fetchAllAnalytes = async () => {
    try {
      const response = await analytesApi.getAll();
      setAllAnalytes(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Error al cargar analitos");
      setLoading(false);
    }
  };

  const loadStudyAnalytes = async (studyId) => {
    try {
      const response = await studyAnalytesApi.getAll(studyId);
      // response.data contains analytes with relation_id
      setPendingAnalytes(response.data);
      setOriginalAnalytes(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error("Error al cargar analitos del estudio");
      setPendingAnalytes([]);
      setOriginalAnalytes([]);
    }
  };

  // Filter all analytes based on search
  const filteredAllAnalytes = allAnalytes.filter(analyte => {
    if (!analyteSearchTerm) return true;
    const searchLower = analyteSearchTerm.toLowerCase();
    return (
      analyte.name?.toLowerCase().includes(searchLower) ||
      analyte.code?.toLowerCase().includes(searchLower)
    );
  });

  // Filter studies for search
  const filteredStudies = studies.filter(study => {
    if (!studySearchTerm) return false;
    const searchLower = studySearchTerm.toLowerCase();
    return (
      study.name?.toLowerCase().includes(searchLower) ||
      study.code?.toLowerCase().includes(searchLower)
    );
  });

  // Filter analytes for adding (exclude already added ones)
  const filteredAnalytesForAdding = allAnalytes.filter(analyte => {
    if (!addAnalyteSearchTerm) return false;
    const searchLower = addAnalyteSearchTerm.toLowerCase();
    const matchesSearch = (
      analyte.name?.toLowerCase().includes(searchLower) ||
      analyte.code?.toLowerCase().includes(searchLower)
    );
    const notAlreadyAdded = !pendingAnalytes.some(pa => pa.id === analyte.id);
    return matchesSearch && notAlreadyAdded;
  });

  const handleOpenDialog = (analyte = null) => {
    if (analyte) {
      setEditingAnalyte(analyte);
      setFormData({
        name: analyte.name,
        code: analyte.code,
        result_type: analyte.result_type || "referenced_value",
        unit: analyte.unit || "",
        reference_min: analyte.reference_min != null ? analyte.reference_min.toString() : "",
        reference_max: analyte.reference_max != null ? analyte.reference_max.toString() : "",
        reference_text: analyte.reference_text || "",
        text_options: analyte.text_options || "",
        age_sex_references: analyte.age_sex_references || [],
        document_template: analyte.document_template || "",
        active: analyte.active,
      });
    } else {
      setEditingAnalyte(null);
      setFormData({
        name: "",
        code: "",
        result_type: "referenced_value",
        unit: "",
        reference_min: "",
        reference_max: "",
        reference_text: "",
        text_options: "",
        age_sex_references: [],
        document_template: "",
        active: true,
      });
    }
    setDialogOpen(true);
  };

  // Add age/sex reference
  const addAgeSexReference = () => {
    setFormData(prev => ({
      ...prev,
      age_sex_references: [
        ...prev.age_sex_references,
        {
          age_min_value: 0,
          age_min_unit: "days",
          age_max_value: 12,
          age_max_unit: "months",
          sex: "both",
          reference_min: null,
          reference_max: null,
          reference_text: "",
        }
      ]
    }));
  };

  // Update age/sex reference
  const updateAgeSexReference = (index, field, value) => {
    setFormData(prev => {
      const newRefs = [...prev.age_sex_references];
      newRefs[index] = { ...newRefs[index], [field]: value };
      return { ...prev, age_sex_references: newRefs };
    });
  };

  // Remove age/sex reference
  const removeAgeSexReference = (index) => {
    setFormData(prev => ({
      ...prev,
      age_sex_references: prev.age_sex_references.filter((_, i) => i !== index)
    }));
  };

  // Format age for display
  const formatAgeWithUnit = (value, unit) => {
    const unitLabels = { days: "días", months: "meses", years: "años" };
    return `${value} ${unitLabels[unit] || unit}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Process age_sex_references to ensure correct values
      const processedReferences = formData.age_sex_references.map(ref => ({
        ...ref,
        age_min_value: parseInt(ref.age_min_value) || 0,
        age_max_value: parseInt(ref.age_max_value) || 0,
        reference_min: ref.reference_min !== "" && ref.reference_min != null ? parseFloat(ref.reference_min) : null,
        reference_max: ref.reference_max !== "" && ref.reference_max != null ? parseFloat(ref.reference_max) : null,
      }));

      const dataToSend = {
        ...formData,
        reference_min: formData.reference_min !== "" && formData.reference_min != null ? parseFloat(formData.reference_min) : null,
        reference_max: formData.reference_max !== "" && formData.reference_max != null ? parseFloat(formData.reference_max) : null,
        age_sex_references: processedReferences,
      };

      if (editingAnalyte) {
        await analytesApi.update(editingAnalyte.id, dataToSend);
        toast.success("Analito actualizado");
      } else {
        await analytesApi.create(dataToSend);
        toast.success("Analito creado");
      }
      setDialogOpen(false);
      fetchAllAnalytes();
    } catch (error) {
      toast.error("Error al guardar analito");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar este analito?")) return;
    try {
      await analytesApi.delete(id);
      toast.success("Analito eliminado");
      fetchAllAnalytes();
      // Also remove from pending if present
      setPendingAnalytes(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Select study from search
  const selectStudy = (study) => {
    // Check for unsaved changes
    if (hasChanges) {
      if (!confirm("Hay cambios sin guardar. ¿Desea continuar y perder los cambios?")) {
        return;
      }
    }
    setSelectedStudy(study.id);
    setStudySearchTerm("");
    setShowStudyDropdown(false);
  };

  // Add analyte to pending list (not saved yet)
  const addAnalyteToPending = (analyte) => {
    // Check if already in pending
    if (pendingAnalytes.some(pa => pa.id === analyte.id)) {
      toast.error("Este analito ya está en la lista");
      return;
    }
    
    setPendingAnalytes(prev => [...prev, analyte]);
    setAddAnalyteSearchTerm("");
    setShowAddAnalyteDropdown(false);
    toast.info(`${analyte.name} agregado a la lista`);
  };

  // Remove analyte from pending list (not saved yet)
  const removeAnalyteFromPending = (analyteId) => {
    const analyte = pendingAnalytes.find(a => a.id === analyteId);
    setPendingAnalytes(prev => prev.filter(a => a.id !== analyteId));
    if (analyte) {
      toast.info(`${analyte.name} removido de la lista`);
    }
  };

  // Move analyte up in the list
  const moveAnalyteUp = (index) => {
    if (index === 0) return; // Already at top
    setPendingAnalytes(prev => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
      return newList;
    });
    setHasChanges(true);
  };

  // Move analyte down in the list
  const moveAnalyteDown = (index) => {
    if (index === pendingAnalytes.length - 1) return; // Already at bottom
    setPendingAnalytes(prev => {
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
      return newList;
    });
    setHasChanges(true);
  };

  // Save changes to the database
  const saveChanges = async () => {
    if (!selectedStudy) return;
    
    setSaving(true);
    try {
      // Get current relations from database
      const currentResponse = await studyAnalytesApi.getAll(selectedStudy);
      const currentRelations = currentResponse.data;
      
      // Find relations to delete (in current but not in pending)
      const pendingIds = new Set(pendingAnalytes.map(a => a.id));
      const relationsToDelete = currentRelations.filter(r => !pendingIds.has(r.id));
      
      // Find analytes to add (in pending but not in current)
      const currentIds = new Set(currentRelations.map(r => r.id));
      const analytesToAdd = pendingAnalytes.filter(a => !currentIds.has(a.id));
      
      // Delete all current relations (we'll re-add them with correct order)
      for (const rel of currentRelations) {
        if (rel.relation_id) {
          await studyAnalytesApi.remove(rel.relation_id);
        }
      }
      
      // Add all analytes with correct order
      for (let i = 0; i < pendingAnalytes.length; i++) {
        const analyte = pendingAnalytes[i];
        await studyAnalytesApi.assign(selectedStudy, analyte.id, i);
      }
      
      // Reload to get updated relation_ids
      await loadStudyAnalytes(selectedStudy);
      
      toast.success("Cambios guardados correctamente");
    } catch (error) {
      toast.error("Error al guardar cambios");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const discardChanges = () => {
    setPendingAnalytes([...originalAnalytes]);
    toast.info("Cambios descartados");
  };

  // Keyboard navigation for study search
  const handleStudySearchKeyDown = (e) => {
    if (!showStudyDropdown || filteredStudies.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedStudyIndex(prev => 
          prev < filteredStudies.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedStudyIndex(prev => 
          prev > 0 ? prev - 1 : filteredStudies.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredStudies[highlightedStudyIndex]) {
          selectStudy(filteredStudies[highlightedStudyIndex]);
        }
        break;
      case 'Escape':
        setShowStudyDropdown(false);
        break;
    }
  };

  // Keyboard navigation for add analyte search
  const handleAddAnalyteKeyDown = (e) => {
    if (!showAddAnalyteDropdown || filteredAnalytesForAdding.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedAnalyteIndex(prev => 
          prev < filteredAnalytesForAdding.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedAnalyteIndex(prev => 
          prev > 0 ? prev - 1 : filteredAnalytesForAdding.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredAnalytesForAdding[highlightedAnalyteIndex]) {
          addAnalyteToPending(filteredAnalytesForAdding[highlightedAnalyteIndex]);
        }
        break;
      case 'Escape':
        setShowAddAnalyteDropdown(false);
        break;
    }
  };

  // Get selected study name
  const getSelectedStudyName = () => {
    const study = studies.find(s => s.id === selectedStudy);
    return study?.name || "";
  };

  return (
    <div className="space-y-6" data-testid="analytes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Analitos
          </h1>
          <p className="text-muted-foreground">
            Gestione los analitos y asígnelos a diferentes estudios
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-analyte-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Analito
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel: All Analytes (Master List) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Microscope className="h-5 w-5" />
              Todos los Analitos
            </CardTitle>
            {/* Search all analytes */}
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar analito por nombre o código..."
                value={analyteSearchTerm}
                onChange={(e) => setAnalyteSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-all-analytes"
              />
              {analyteSearchTerm && (
                <button 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setAnalyteSearchTerm("")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground mb-2">
              {filteredAllAnalytes.length} analito(s) encontrado(s)
            </div>
            <div className="border rounded-lg max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Cargando...
                </div>
              ) : filteredAllAnalytes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {analyteSearchTerm ? "No se encontraron analitos" : "No hay analitos registrados"}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Unidad</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAllAnalytes.map((analyte) => (
                      <TableRow key={analyte.id}>
                        <TableCell className="font-mono text-sm">{analyte.code}</TableCell>
                        <TableCell className="font-medium">{analyte.name}</TableCell>
                        <TableCell className="text-sm">{analyte.unit || "-"}</TableCell>
                        <TableCell className="text-sm">
                          {analyte.reference_text || 
                            (analyte.reference_min != null && analyte.reference_max != null
                              ? `${analyte.reference_min} - ${analyte.reference_max}`
                              : analyte.reference_min != null && analyte.reference_max == null
                                ? `Mayor a ${analyte.reference_min}`
                                : analyte.reference_min == null && analyte.reference_max != null
                                  ? `Hasta ${analyte.reference_max}`
                                  : "-")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenDialog(analyte)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(analyte.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right Panel: Study Analytes Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link2 className="h-5 w-5" />
              Asignar Analitos a Estudio
            </CardTitle>
            
            {/* Study search */}
            <div className="relative mt-2" ref={studySearchRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar estudio..."
                value={studySearchTerm}
                onChange={(e) => {
                  setStudySearchTerm(e.target.value);
                  setShowStudyDropdown(e.target.value.length > 0);
                  setHighlightedStudyIndex(0);
                }}
                onFocus={() => studySearchTerm && setShowStudyDropdown(true)}
                onKeyDown={handleStudySearchKeyDown}
                className="pl-10"
                data-testid="search-study"
              />
              {studySearchTerm && (
                <button 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setStudySearchTerm("");
                    setShowStudyDropdown(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              
              {/* Study dropdown */}
              {showStudyDropdown && filteredStudies.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredStudies.map((study, index) => (
                    <button
                      key={study.id}
                      className={`w-full px-4 py-2 text-left flex justify-between items-center ${
                        index === highlightedStudyIndex 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => selectStudy(study)}
                      onMouseEnter={() => setHighlightedStudyIndex(index)}
                    >
                      <span>
                        {study.code && <span className={`font-mono mr-2 ${index === highlightedStudyIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{study.code}</span>}
                        {study.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selected study display */}
            {selectedStudy && (
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="secondary" className="text-sm">
                  <FlaskConical className="h-3 w-3 mr-1" />
                  {getSelectedStudyName()}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-muted-foreground"
                  onClick={() => {
                    if (hasChanges && !confirm("Hay cambios sin guardar. ¿Desea continuar?")) return;
                    setSelectedStudy("");
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
                {hasChanges && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    Sin guardar
                  </Badge>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedStudy ? (
              <div className="text-center py-8 text-muted-foreground">
                Busque y seleccione un estudio para gestionar sus analitos
              </div>
            ) : (
              <div className="space-y-4">
                {/* Add analyte search */}
                <div className="relative" ref={addAnalyteSearchRef}>
                  <Label className="text-sm font-medium mb-2 block">Agregar analito</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar analito para agregar..."
                      value={addAnalyteSearchTerm}
                      onChange={(e) => {
                        setAddAnalyteSearchTerm(e.target.value);
                        setShowAddAnalyteDropdown(e.target.value.length > 0);
                        setHighlightedAnalyteIndex(0);
                      }}
                      onFocus={() => addAnalyteSearchTerm && setShowAddAnalyteDropdown(true)}
                      onKeyDown={handleAddAnalyteKeyDown}
                      className="pl-10"
                      data-testid="search-add-analyte"
                    />
                    {addAnalyteSearchTerm && (
                      <button 
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setAddAnalyteSearchTerm("");
                          setShowAddAnalyteDropdown(false);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    
                    {/* Add analyte dropdown */}
                    {showAddAnalyteDropdown && filteredAnalytesForAdding.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredAnalytesForAdding.map((analyte, index) => (
                          <button
                            key={analyte.id}
                            className={`w-full px-4 py-2 text-left flex justify-between items-center ${
                              index === highlightedAnalyteIndex 
                                ? 'bg-primary text-primary-foreground' 
                                : 'hover:bg-muted'
                            }`}
                            onClick={() => addAnalyteToPending(analyte)}
                            onMouseEnter={() => setHighlightedAnalyteIndex(index)}
                          >
                            <span>
                              <span className={`font-mono mr-2 ${index === highlightedAnalyteIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{analyte.code}</span>
                              {analyte.name}
                            </span>
                            {analyte.unit && (
                              <span className={`text-xs ${index === highlightedAnalyteIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                {analyte.unit}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {showAddAnalyteDropdown && addAnalyteSearchTerm && filteredAnalytesForAdding.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                        No se encontraron analitos disponibles
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    ↑↓ navegar, Enter seleccionar
                  </p>
                </div>

                {/* Study analytes list */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Analitos del estudio ({pendingAnalytes.length})
                  </Label>
                  <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                    {pendingAnalytes.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No hay analitos asignados a este estudio
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12 text-center">#</TableHead>
                            <TableHead className="w-20">Orden</TableHead>
                            <TableHead>Código</TableHead>
                            <TableHead>Nombre</TableHead>
                            <TableHead>Referencia</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingAnalytes.map((analyte, index) => (
                            <TableRow key={analyte.id} className="group">
                              <TableCell className="text-center font-mono text-muted-foreground">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => moveAnalyteUp(index)}
                                    disabled={index === 0}
                                    title="Mover arriba"
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => moveAnalyteDown(index)}
                                    disabled={index === pendingAnalytes.length - 1}
                                    title="Mover abajo"
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-sm">{analyte.code}</TableCell>
                              <TableCell className="font-medium">{analyte.name}</TableCell>
                              <TableCell className="text-sm">
                                {analyte.reference_text ||
                                  (analyte.reference_min != null && analyte.reference_max != null
                                    ? `${analyte.reference_min} - ${analyte.reference_max} ${analyte.unit || ''}`
                                    : analyte.reference_min != null && analyte.reference_max == null
                                      ? `Mayor a ${analyte.reference_min} ${analyte.unit || ''}`
                                      : analyte.reference_min == null && analyte.reference_max != null
                                        ? `Hasta ${analyte.reference_max} ${analyte.unit || ''}`
                                        : analyte.unit || "-")}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => removeAnalyteFromPending(analyte.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use las flechas ↑↓ para cambiar el orden de los analitos
                  </p>
                </div>

                {/* Save/Discard buttons */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  {hasChanges && (
                    <Button
                      variant="outline"
                      onClick={discardChanges}
                    >
                      Descartar cambios
                    </Button>
                  )}
                  <Button
                    onClick={saveChanges}
                    disabled={!hasChanges || saving}
                    data-testid="save-study-analytes-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnalyte ? "Editar Analito" : "Nuevo Analito"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  required
                  data-testid="analyte-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="analyte-name"
                />
              </div>
            </div>

            {/* Result Type */}
            <div className="space-y-2">
              <Label>Tipo de Resultado *</Label>
              <Select
                value={formData.result_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, result_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subtitle">
                    <div className="flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      <span>Subtítulo</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="text">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>Texto</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="referenced_value">
                    <div className="flex items-center gap-2">
                      <Calculator className="h-4 w-4" />
                      <span>Valor Referenciado</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="document">
                    <div className="flex items-center gap-2">
                      <File className="h-4 w-4" />
                      <span>Documento</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.result_type === "subtitle" && "Solo muestra un texto como separador, sin capturar valor"}
                {formData.result_type === "text" && "Permite ingresar texto libre como resultado"}
                {formData.result_type === "referenced_value" && "Valor numérico con rangos de referencia por edad y sexo"}
                {formData.result_type === "document" && "Permite crear un documento con formato enriquecido"}
              </p>
            </div>

            {/* Conditional fields based on result type */}
            {formData.result_type === "referenced_value" && (
              <>
                {/* Unit field */}
                <div className="space-y-2">
                  <Label>Unidad de medida</Label>
                  <Input
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    placeholder="mg/dL, g/L, %, etc."
                    data-testid="analyte-unit"
                  />
                </div>

                {/* Default reference (when no age/sex specific) */}
                <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                  <h4 className="font-medium text-sm">Referencia General (cuando no aplica por edad/sexo)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Mínimo</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.reference_min}
                        onChange={(e) => setFormData(prev => ({ ...prev, reference_min: e.target.value }))}
                        placeholder="0"
                        data-testid="analyte-ref-min"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Máximo</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.reference_max}
                        onChange={(e) => setFormData(prev => ({ ...prev, reference_max: e.target.value }))}
                        placeholder="100"
                        data-testid="analyte-ref-max"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Texto (opcional)</Label>
                      <Input
                        value={formData.reference_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, reference_text: e.target.value }))}
                        placeholder="Negativo, Positivo..."
                        data-testid="analyte-ref-text"
                      />
                    </div>
                  </div>
                </div>

                {/* Age/Sex specific references */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Referencias por Edad y Sexo</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addAgeSexReference}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar Rango
                    </Button>
                  </div>

                  {formData.age_sex_references.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4 border-2 border-dashed rounded-lg">
                      Sin referencias específicas. Se usará la referencia general.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {formData.age_sex_references.map((ref, index) => (
                        <div key={index} className="p-3 border rounded-lg bg-background space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Rango #{index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              onClick={() => removeAgeSexReference(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {/* Age range with unit selection */}
                          <div className="grid grid-cols-5 gap-2">
                            {/* Minimum Age */}
                            <div className="space-y-1">
                              <Label className="text-xs">Edad Mínima</Label>
                              <Input
                                type="number"
                                min="0"
                                value={ref.age_min_value ?? 0}
                                onChange={(e) => updateAgeSexReference(index, 'age_min_value', e.target.value === '' ? 0 : parseInt(e.target.value))}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Unidad</Label>
                              <Select
                                value={ref.age_min_unit || "days"}
                                onValueChange={(value) => updateAgeSexReference(index, 'age_min_unit', value)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="days">Días</SelectItem>
                                  <SelectItem value="months">Meses</SelectItem>
                                  <SelectItem value="years">Años</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Maximum Age */}
                            <div className="space-y-1">
                              <Label className="text-xs">Edad Máxima</Label>
                              <Input
                                type="number"
                                min="0"
                                value={ref.age_max_value ?? 0}
                                onChange={(e) => updateAgeSexReference(index, 'age_max_value', e.target.value === '' ? 0 : parseInt(e.target.value))}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Unidad</Label>
                              <Select
                                value={ref.age_max_unit || "months"}
                                onValueChange={(value) => updateAgeSexReference(index, 'age_max_unit', value)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="days">Días</SelectItem>
                                  <SelectItem value="months">Meses</SelectItem>
                                  <SelectItem value="years">Años</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Sex */}
                            <div className="space-y-1">
                              <Label className="text-xs">Sexo</Label>
                              <Select
                                value={ref.sex}
                                onValueChange={(value) => updateAgeSexReference(index, 'sex', value)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="both">Ambos</SelectItem>
                                  <SelectItem value="M">Masculino</SelectItem>
                                  <SelectItem value="F">Femenino</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* Preview of range */}
                          <p className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                            Rango: {formatAgeWithUnit(ref.age_min_value || 0, ref.age_min_unit || "days")} - {formatAgeWithUnit(ref.age_max_value || 0, ref.age_max_unit || "months")} 
                            {ref.sex === "M" ? " (Masculino)" : ref.sex === "F" ? " (Femenino)" : " (Ambos sexos)"}
                          </p>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Ref. Mínimo</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={ref.reference_min != null ? ref.reference_min : ""}
                                onChange={(e) => updateAgeSexReference(index, 'reference_min', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Ref. Máximo</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={ref.reference_max != null ? ref.reference_max : ""}
                                onChange={(e) => updateAgeSexReference(index, 'reference_max', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Texto (opcional)</Label>
                              <Input
                                value={ref.reference_text || ""}
                                onChange={(e) => updateAgeSexReference(index, 'reference_text', e.target.value)}
                                className="h-8"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {formData.result_type === "text" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Unidad de medida</Label>
                  <Input
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    placeholder="Ej: %, mg/dL, etc."
                  />
                  <p className="text-xs text-muted-foreground">
                    Opcional: unidad que aparecerá junto al resultado
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Referencia</Label>
                  <Input
                    value={formData.reference_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference_text: e.target.value }))}
                    placeholder="Ej: Negativo"
                  />
                  <p className="text-xs text-muted-foreground">
                    Este texto aparecerá como valor de referencia en los resultados. Usa {"<>"} para separar en líneas.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Resultados posibles (separados por coma)</Label>
                  <Input
                    value={formData.text_options}
                    onChange={(e) => setFormData(prev => ({ ...prev, text_options: e.target.value }))}
                    placeholder="Ej: Positivo,Negativo,Indeterminado"
                  />
                  <p className="text-xs text-muted-foreground">
                    Si agregas opciones separadas por coma, se mostrarán como lista desplegable al capturar. 
                    Si dejas vacío, se mostrará un campo de texto libre.
                  </p>
                  {formData.text_options && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      <span className="text-xs text-muted-foreground">Vista previa:</span>
                      {formData.text_options.split(',').map((opt, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {opt.trim()}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {formData.result_type === "document" && (
              <div className="space-y-2">
                <Label>Plantilla del Documento</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Crea la plantilla que se usará para capturar este tipo de resultado
                </p>
                <RichTextEditor
                  content={formData.document_template}
                  onChange={(html) => setFormData(prev => ({ ...prev, document_template: html }))}
                  placeholder="Escribe el contenido del documento aquí..."
                />
              </div>
            )}

            {formData.result_type === "subtitle" && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> Los subtítulos se muestran como separadores en los resultados. 
                  No se captura ningún valor para este tipo de analito.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-analyte-btn">
                {editingAnalyte ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
